// The Family Portal (#543): its launch switch, its words, and the rules of
// its invitations (#770), for the web and, through the mirror under
// functions/domain/, the Cloud Functions.
//
// The switch and every rule are the core's (core/src/family_portal.rs, whose
// header says what the switch hides and why it lives there). This is the
// web's one reading of them, so nothing else here asks the core directly and
// a test can put the switch in either position without editing the constant.

import { coreSync } from './core.js';
import { BRAND } from './brand.js';
import { portalPath } from './identity.js';

// A test's position for the switch, or null for the checked-in value.
let forced = null;

/** Whether the Family Portal is open. */
export function familyPortalOpen() {
  return forced ?? coreSync().familyPortalOpen();
}

/**
 * Tests only: put the switch on (`true`) or off (`false`), or back to the
 * checked-in value (`null`). Nothing in the app calls this; the server's
 * callables take the switch as an argument as well, so a browser that called
 * it would only redraw its own screens — every refusal that matters is the
 * server's, against its own copy of the constant.
 */
export function setFamilyPortalOpenForTests(open) {
  forced = open == null ? null : open === true;
}

/** A Family Portal word by code (core/src/family_portal.rs `WORDING`). */
export function familyPortalWord(code) {
  return coreSync().familyPortalWord(String(code || ''));
}

const str = (v) => (v == null ? null : String(v));

// ── Access (#770) ───────────────────────────────────────────────────────────
//
// Literals, because a Cloud Function reads them at module scope;
// test/core/familyPortal.wasm.test.js holds them to the core's.

/** A contact's access, as the teacher sees it. */
export const ACCESS = Object.freeze({
  notInvited: 'not_invited',
  invited: 'invited',
  active: 'active',
  revoked: 'revoked',
  // The family deleted its own login (#774); the server sets it on each
  // studio's record that was active (functions/deletion/store.js).
  loginDeleted: 'login_deleted',
});

/** What a teacher may do about a contact's access. */
export const ACCESS_ACTIONS = Object.freeze({
  enable: 'enable',
  resend: 'resend',
  revoke: 'revoke',
  // A new invitation after the family deleted its login (#774): the same
  // callable as enable.
  inviteAgain: 'invite_again',
});

/** The status an access record says, or `not_invited` for none. */
export const familyPortalAccessStatus = (stored) => coreSync().familyPortalAccessStatus(str(stored));

/** `users/{studio}/portalAccess/{id}` for one contact. */
export const familyPortalAccessId = (familyId, contactId) => coreSync().familyPortalAccessId(String(familyId), String(contactId));

/** `portalMembers/{id}` for one contact of one studio's family. */
export const familyPortalMemberId = (studioUid, familyId, contactId) => (
  coreSync().familyPortalMemberId(String(studioUid), String(familyId), String(contactId))
);

/** The outbox id of one invitation, so a retried call queues it once. */
export const familyPortalInviteMessageId = (familyId, contactId, seq) => (
  coreSync().familyPortalInviteMessageId(String(familyId), String(contactId), Number(seq) || 0)
);

/** The buttons a contact's row offers, in order. */
export const familyPortalAccessActions = (status, hasEmail) => (
  [...coreSync().familyPortalAccessActions(String(status || ''), Boolean(hasEmail))]
);

/** An address as two of them are compared, or null for none. */
export const familyPortalEmailKey = (email) => coreSync().familyPortalEmailKey(str(email)) || null;

/** A refusal's sentence, by the code the server answers with. */
export const familyPortalRefusal = (code) => coreSync().familyPortalRefusal(String(code || ''));

// ── Cancelling (#775) ───────────────────────────────────────────────────────
//
// The rules are the core's (core/src/family_portal.rs, "Cancelling"); the
// callable and the view that offer a cancel are functions/portal/cancel.js
// and cancelPolicy.js.

/** The two sides of the cancellation deadline. */
export const CANCEL_SIDES = Object.freeze({ before: 'before', after: 'after' });

/**
 * Which side of the deadline `nowMs` is for a lesson starting at `startMs`
 * (epoch ms), the deadline `hours` before it: `before`, `after`, or null
 * once the lesson has started.
 */
export const familyPortalCancelSide = (startMs, nowMs, hours) => (
  coreSync().familyPortalCancelSide(Number(startMs), Number(nowMs), Number(hours) || 0) || null
);

/** What one cancel will do to one student, as the dialog says it before Yes. */
export const familyPortalCancelEffect = ({
  name = null, attendance, billed = false, issuesCredit = false, returnsCredit = false,
}) => coreSync().familyPortalCancelEffect(
  str(name), String(attendance || ''), Boolean(billed), Boolean(issuesCredit), Boolean(returnsCredit),
);

/** The longest Note to Teacher: MMS's 255. */
export const familyPortalCancelNoteMax = () => coreSync().familyPortalCancelNoteMax();

/**
 * What a cancel does, as one code: the side of the deadline and the
 * policy's attendance choice for it. The dialog sends the code it showed;
 * the callable refuses a cancel whose code is no longer the same
 * (`cancel_changed`), so a family is never told one thing and given another.
 */
export const familyPortalCancelCode = (side, attendance) => `${side}:${attendance}`;

// ── Rescheduling (#543) ─────────────────────────────────────────────────────
//
// The rules are the core's (core/src/family_portal.rs, "Rescheduling"); the
// callable is functions/portal/reschedule.js.

/**
 * The attendance a reschedule's first half takes for the policy's choice:
 * the same, unless it issues a make-up credit, which a reschedule never
 * does (#543).
 */
export const familyPortalRescheduleAttendance = (policyAttendance) => (
  coreSync().familyPortalRescheduleAttendance(String(policyAttendance || ''))
);

/**
 * What a reschedule does, as one code: the side, the first half's
 * attendance, and whether the credit covers the new time. The dialog sends
 * back the code of the time it showed; the callable refuses a reschedule
 * whose code has changed (`reschedule_changed`).
 */
export const familyPortalRescheduleCode = (side, attendance, covered) => (
  coreSync().familyPortalRescheduleCode(String(side || ''), String(attendance || ''), Boolean(covered))
);

/**
 * The dialog's sentence for the new time's credit: the figures already
 * written in the studio's Region, `price` as money or null.
 */
export const familyPortalRescheduleCreditLine = ({
  name = null, credits, before, after, covered, price = null,
}) => coreSync().familyPortalRescheduleCreditLine(
  str(name), String(credits ?? ''), String(before ?? ''), String(after ?? ''), Boolean(covered), str(price),
);

/** What a reschedule does to the lesson being moved, as the dialog says it. */
export const familyPortalRescheduleEffect = ({
  name = null, attendance, billed = false, returnsCredit = false,
}) => coreSync().familyPortalRescheduleEffect(str(name), String(attendance || ''), Boolean(billed), Boolean(returnsCredit));

export const inviteTtlDays = () => coreSync().familyPortalInviteTtlDays();
export const inviteCooldownSeconds = () => coreSync().familyPortalInviteCooldownSeconds();
export const passwordMin = () => coreSync().familyPortalPasswordMin();

// ── The invitation ──────────────────────────────────────────────────────────

/** Where a family signs in again: the app's `/portal`. */
export const familyPortalLoginUrl = () => coreSync().familyPortalLoginUrl(BRAND.domain);

/** The join page's path in the app (`/portal/join`). */
export const familyPortalJoinPath = () => coreSync().familyPortalJoinPath();

/** One invitation's link, carrying its token in the fragment. */
export const familyPortalJoinUrl = (token) => coreSync().familyPortalJoinUrl(BRAND.domain, String(token));

/** The token a join link carries, from `location.hash`, or null. */
export const familyPortalTokenFromHash = (hash) => coreSync().familyPortalTokenFromHash(String(hash || '')) || null;

/** What the preview and the outbox row show where the link goes. */
export const familyPortalSetupPlaceholder = () => coreSync().familyPortalSetupPlaceholder();

export const familyPortalInviteSubject = () => coreSync().familyPortalInviteSubject();

/**
 * The preview's From line: the studio's name and its Email & SMS address,
 * `Name <address>`, or the name alone without one (#771). The core's rule,
 * so both phones' previews read the same.
 */
export const familyPortalInviteFrom = (studioName, studioEmail) => (
  coreSync().familyPortalInviteFrom(str(studioName), str(studioEmail))
);

// ── The portal's pages (#772) ───────────────────────────────────────────────
//
// Under the core's `/portal` (`portalPath`, core/src/identity.rs), web-only:
// the phones have no portal (a portal login gets only the studio selector
// there, mobile/WONKY.md 167).

/** The family's sign-in page: no trial and no sign-up offer (#771). */
export const familyPortalSignInPath = () => `${portalPath()}/signin`;

/**
 * Where a family lands after deleting its own login (#774): signed out,
 * with nothing left to read, so it is a page of its own rather than the
 * sign-in page.
 */
export const familyPortalDeletedPath = () => `${portalPath()}/deleted`;

/** MMS's Calendar → Schedule. */
export const familyPortalSchedulePath = () => `${portalPath()}/schedule`;

/** MMS's Calendar → Find & Book (#773). */
export const familyPortalBookPath = () => `${portalPath()}/book`;

/** One studio's family, as the portal names a choice of one. */
export const familyPortalChoiceKey = (studioUid, familyId) => `${studioUid}__${familyId}`;

// A membership row as the core reads it: strings or null, nothing else.
const membershipRow = (r) => ({
  id: str(r?.id),
  studioUid: str(r?.studioUid),
  familyId: str(r?.familyId),
  studioName: str(r?.studioName),
  familyName: str(r?.familyName),
  status: str(r?.status),
});

/**
 * What a login may open, from its memberships (src/data/identity.js
 * `listPortalMemberships`): one choice per studio's family, revoked rows
 * left out (#770: a revoked membership is the studio's archive and gives the
 * login nothing), and a family two of whose contacts are the same person
 * listed once. Sorted by studio, then family, so the picker reads the same
 * every time. The names are the ones the server wrote at joining, and either
 * may be missing.
 *
 * The rule is the core's (`portal_choices`, core/src/identity.rs, "A login's
 * studios"), shared with the phones' studio selector so the two never
 * disagree about which studios a login belongs to.
 */
export function familyPortalChoices(rows = []) {
  const list = (rows || []).filter((r) => r && typeof r === 'object').map(membershipRow);
  return [...coreSync().portalChoices(list)].map((c) => ({
    key: c.key,
    studioUid: c.studioUid,
    familyId: c.familyId,
    studioName: c.studioName ?? null,
    familyName: c.familyName ?? null,
  }));
}

/** The invitation's words, MMS's Login Setup template with our name. */
export function familyPortalInviteText({
  firstName = null, email, setupUrl, loginUrl = familyPortalLoginUrl(), teacherName = null, studioName = null,
}) {
  return coreSync().familyPortalInviteText(
    str(firstName), String(email || ''), String(setupUrl || ''), String(loginUrl || ''), str(teacherName), str(studioName),
  );
}
